/**
 * Leave Correction Sheet (Epic 2, Verlof correctie — Story 2.4, UX-DR8/14/15)
 *
 * Custom modal, zelfde overlay+sheet-patroon als `LeaveRequestSheet.tsx`
 * (dit project heeft geen shadcn/ui). Toont een reeds goedgekeurde
 * verlofaanvraag (`historyEntry`) met een niet-bewerkbaar kader voor de
 * huidige waarde, en laat de gebruiker een correctie indienen die pas na
 * beoordeling (Story 2.6) de geldende waarde op `nmbrs_leave_requests`
 * wordt.
 *
 * AD-11 (hard): het urenbegrip van de ORSPRONKELIJKE aanvraag bepaalt of dit
 * scherm tijdvelden of een urenveld toont — nooit gemengd, nooit een keuze
 * die de gebruiker zelf maakt.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  findScheduleEntryForDate,
  maxCorrigeerbareUren,
  urenUitTijdvak,
  type OwnLeaveHistoryEntry,
} from '@functieloonhuis/shared';
import { useAuth } from '../auth';
import { useEmployeeSchedule, useHolidays } from '../werkrooster/hooks/useWorkSchedules';
import { usePauzeVenster } from '../settings/useSettings';
import { useSubmitLeaveCorrection } from './hooks/useSubmitLeaveCorrection';
import { ApiRequestError } from '../../lib/api';
import { formatNlDagEnMaand, formatNlMaandNaam } from './correctieTermijnCopy';

/**
 * UX-DR14 (microcopy-tabel): NL-getalnotatie voor uren — hele getallen
 * zonder decimaal (`1`), anders één decimaal met komma (`1,5`). Zelfde regel
 * als `LeaveRequestSheet.tsx`'s lokale `formatNlUren`; hier opnieuw lokaal
 * gehouden om dezelfde reden (AD-5 gaat over de urenberekening, niet over de
 * weergave ervan).
 */
function formatNlUren(uren: number): string {
  const rounded = Math.round(uren * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',');
}

function timeToHours(time: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time);
  if (!match) return null;
  return Number(match[1]) + Number(match[2]) / 60;
}

/**
 * Bruto-uren van de roosterspan zelf, zonder pauze-aftrek — uitsluitend voor
 * de foutmelding bij AC-4/AC-5 ("Je werkte 8,5 uur op ..."), die bewust het
 * bruto-rooster noemt terwijl `maxCorrigeerbareUren` de NETTO grens teruggeeft.
 */
function grossRoosterUren(roosterspan: { start: string; end: string } | null): number | null {
  if (!roosterspan) return null;
  const start = timeToHours(roosterspan.start);
  const end = timeToHours(roosterspan.end);
  if (start === null || end === null || end <= start) return null;
  return end - start;
}

/** UX-DR14: "6 augustus" — dag + volledige maandnaam, geen jaartal. */
function formatNlDatum(iso: string): string {
  const parts = iso.split('-').map(Number);
  const year = parts[0] ?? 1970;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

interface LeaveCorrectionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
  /** Eigen-scope only (Taak 9) — deze sheet opent uitsluitend vanaf de eigen "Mijn verlof"-rij. */
  historyEntry: OwnLeaveHistoryEntry;
  /**
   * Story 2.5 (AC-5, D3 "wat HR extra ziet"): naam van de medewerker van wie
   * dit verlof is, voor de letterlijke uit-dienst-regel. Optioneel en alleen
   * relevant voor een HR/admin-kijker -- op de huidige "Mijn verlof"-pagina
   * (strikt eigen scope, AD-4) is dit altijd de ingelogde gebruiker zelf; een
   * toekomstige HR-correctiepagina (story 4.2) geeft hier een andere naam door.
   */
  employeeName?: string;
  /**
   * Story 2.5 (AC-5, AD-16/B10): `employees.uitDienstDatum` van de medewerker
   * van wie dit verlof is. `null`/`undefined` betekent niet uit dienst. Alleen
   * getoond aan een HR/admin-kijker -- voor iedere andere rol is de correctie-
   * ingang dan al niet bereikbaar (zie `MijnVerlofPage`).
   */
  employeeUitDienstDatum?: string | null;
}

export function LeaveCorrectionSheet({
  isOpen,
  onClose,
  onSubmitted,
  historyEntry,
  employeeName,
  employeeUitDienstDatum,
}: LeaveCorrectionSheetProps) {
  const { user, isTeamManagerOrHR, hasRole } = useAuth();
  // AC-4/AC-5: deze twee waarschuwingen zijn specifiek voor HR/admin (D3) --
  // `isTeamManagerOrHR` is hier te breed (ook een gewone leidinggevende), dus
  // bewust een aparte, striktere afleiding.
  const viewerIsHrOrAdmin = hasRole(['hr', 'admin']);
  // AD-11: beide gevuld betekent tijden-gebaseerd, anders dagdeel-gebaseerd — nooit gemengd.
  const usesTijden = historyEntry.startTime !== null && historyEntry.endTime !== null;

  const [hoursInput, setHoursInput] = useState<string>(String(historyEntry.hours));
  const [vanTijd, setVanTijd] = useState<string>(historyEntry.startTime ?? '');
  const [totTijd, setTotTijd] = useState<string>(historyEntry.endTime ?? '');
  // Story 2.7 (AC-1, B23): bij kort verlof (tijden-gebaseerd) laat "het ging
  // helemaal niet door" zich niet als tijdvak van nul uur uitdrukken (FR-2
  // weigert dat) -- deze knop zet de uitkomst rechtstreeks op nul, los van
  // wat er in Van/Tot staat. Wie de tijden weer aanraakt, geeft daarmee aan
  // dat hij toch een tijdvak bedoelt, dus dan gaat de vlag weer uit.
  const [nulViaKnop, setNulViaKnop] = useState(false);
  const [reason, setReason] = useState('');
  // Voorkomt dat de toelichting-fout al verschijnt vóórdat iemand iets heeft
  // ingevuld (AC-1's eerste render toont nog geen enkele foutmelding). Gaat
  // aan bij ELKE interactie met het formulier -- uren/tijden wijzigen, maar
  // ook de toelichting zelf aanraken (typen of focus+blur) -- zodat AC-3's
  // foutmelding ook verschijnt wanneer iemand de uren ongemoeid laat en
  // direct de toelichting leeg laat staan (code review 2026-08-08, Finding #2).
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const submit = useSubmitLeaveCorrection();
  const { data: scheduleResp } = useEmployeeSchedule(user?.employeeId ?? 0);
  const year = Number(historyEntry.startDate.slice(0, 4));
  const { data: holidaysResp } = useHolidays(year);
  const pauzeVensterQuery = usePauzeVenster();

  useEffect(() => {
    if (!isOpen) return;
    setHoursInput(String(historyEntry.hours));
    setVanTijd(historyEntry.startTime ?? '');
    setTotTijd(historyEntry.endTime ?? '');
    setNulViaKnop(false);
    setReason('');
    setTouched(false);
    setServerError(null);
  }, [isOpen, historyEntry]);

  useEffect(() => {
    if (!isOpen) return;
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submit.isPending) onClose();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [isOpen, onClose, submit.isPending]);

  const scheduleEntries = useMemo(() => {
    const schedule = scheduleResp?.data;
    if (!schedule) return [];
    const weekA = schedule.weekA.map((e) => ({
      ...e,
      weekType: 'A' as const,
      startTime: e.startTime ?? null,
      endTime: e.endTime ?? null,
    }));
    const weekB = schedule.weekB.map((e) => ({
      ...e,
      weekType: 'B' as const,
      startTime: e.startTime ?? null,
      endTime: e.endTime ?? null,
    }));
    return [...weekA, ...weekB];
  }, [scheduleResp]);

  const holidayEntries = holidaysResp?.data ?? [];

  const dayEntry = useMemo(() => {
    if (scheduleEntries.length === 0) return undefined;
    return findScheduleEntryForDate(historyEntry.startDate, scheduleEntries);
  }, [scheduleEntries, historyEntry.startDate]);

  const roosterspan =
    dayEntry && dayEntry.startTime && dayEntry.endTime
      ? { start: dayEntry.startTime, end: dayEntry.endTime }
      : null;

  // B12/AC-4/AC-5/AC-7/AC-8/AC-11: dezelfde pure resolver als de server —
  // de bovengrens volgt altijd het urenbegrip van de OORSPRONKELIJKE aanvraag.
  const maxUren = useMemo(() => {
    if (!pauzeVensterQuery.data) return null;
    return maxCorrigeerbareUren(
      {
        startDate: historyEntry.startDate,
        endDate: historyEntry.endDate,
        startPart: historyEntry.startPart,
        endPart: historyEntry.endPart,
        startTime: historyEntry.startTime,
        endTime: historyEntry.endTime,
      },
      scheduleEntries,
      holidayEntries,
      pauzeVensterQuery.data,
      historyEntry.hours
    );
  }, [historyEntry, scheduleEntries, holidayEntries, pauzeVensterQuery.data]);

  const tijdvakResult = useMemo(() => {
    if (!usesTijden || !vanTijd || !totTijd || !pauzeVensterQuery.data) return null;
    return urenUitTijdvak(vanTijd, totTijd, roosterspan, pauzeVensterQuery.data);
  }, [usesTijden, vanTijd, totTijd, roosterspan, pauzeVensterQuery.data]);

  const newHours = useMemo(() => {
    if (usesTijden) {
      // Story 2.7 (AC-1, B23): de knop wint van het tijdvak -- de tijden
      // blijven zichtbaar, maar tellen niet mee zolang de vlag aan staat.
      if (nulViaKnop) return 0;
      return tijdvakResult && !tijdvakResult.ongeldig ? tijdvakResult.hours : null;
    }
    const normalized = hoursInput.trim().replace(',', '.');
    if (normalized === '') return null;
    const parsed = Number(normalized);
    // Story 2.7 (AC-2, UX-DR9): `0` is een geldige uitkomst -- "verlof laten
    // vervallen" is de natuurlijke uitkomst van nul invullen, geen apart pad.
    return !Number.isNaN(parsed) && parsed >= 0 ? parsed : null;
  }, [usesTijden, nulViaKnop, tijdvakResult, hoursInput]);

  const exceedsMax = newHours !== null && maxUren !== null && newHours > maxUren;
  const trimmedReason = reason.trim();
  const reasonMissing = trimmedReason.length === 0;
  const showReasonError = touched && reasonMissing;

  const grossHours = grossRoosterUren(roosterspan);

  const canSubmit = newHours !== null && !exceedsMax && !reasonMissing && !submit.isPending;

  async function handleSubmit() {
    if (!canSubmit || newHours === null) return;
    setServerError(null);
    try {
      await submit.mutateAsync({
        id: historyEntry.id,
        // Story 2.7 (AC-1): via de knop gaat dit als dagdeel-vorm (`newHours:
        // 0`) de deur uit, ook al is de oorspronkelijke aanvraag tijden-
        // gebaseerd -- een tijdvak van nul uur zou de server toch weigeren
        // (FR-2), en de dienstlaag heeft geen tijdenbasis nodig om naar nul
        // te corrigeren (AD-11 gaat over de BOVENGRENS, niet over dit pad).
        input:
          usesTijden && !nulViaKnop
            ? { newStartTime: vanTijd, newEndTime: totTijd, reason: trimmedReason }
            : { newHours, reason: trimmedReason },
      });
      onSubmitted?.();
      onClose();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setServerError(err.message);
      } else {
        setServerError('Wijziging indienen mislukt. Probeer het opnieuw.');
      }
    }
  }

  if (!isOpen) return null;

  const diff = newHours !== null ? Math.round((newHours - historyEntry.hours) * 100) / 100 : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" data-testid="leave-correction-sheet-overlay">
      <div className="w-full max-w-md h-full bg-white p-6 overflow-y-auto shadow-xl" data-testid="correctie-sheet">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-[#1E293B]">Uren aanpassen</h2>
          <button type="button" onClick={onClose} aria-label="Sluiten" className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        {/* Story 4.3: corrigeert iemand de uren van een ánder (de
            leidinggevende vanuit het teamblok), dan staat hier van wie dit
            verlof is. Op "Mijn verlof" is dat de ingelogde gebruiker zelf en
            zou de regel alleen ruis toevoegen -- vandaar de vergelijking. */}
        {employeeName && employeeName !== user?.name && (
          <p data-testid="correctie-medewerker" className="-mt-4 mb-6 text-sm text-gray-500">
            {employeeName}
          </p>
        )}

        {serverError && (
          <div data-testid="correctie-server-error" className="mb-4 px-3 py-2 rounded-md bg-[#D64545]/10 text-[#D64545] text-sm">
            {serverError}
          </div>
        )}

        {/* AC-4: alleen voor HR/admin -- ziet dat de termijn verstreken is, maar
            kan nog wél corrigeren (de knop hieronder blijft bruikbaar). */}
        {viewerIsHrOrAdmin && historyEntry.correctieTermijnVerstreken === true && (
          <div
            data-testid="correctie-afgesloten-maand-waarschuwing"
            role="status"
            className="mb-4 px-3 py-2 rounded-md bg-[#54949E]/10 text-[#1E293B] text-sm"
          >
            {`${formatNlMaandNaam(historyEntry.endDate)} is afgesloten. Deze wijziging gaat wel naar de loonadministratie, maar mogelijk is het loon over deze periode al uitbetaald.`}
          </div>
        )}

        {/* AC-5: alleen voor HR/admin -- de medewerker zelf en zijn
            leidinggevende komen deze sheet al niet in (MijnVerlofPage
            verbergt de knop dan), dus deze regel is uitsluitend voor de rol
            die nog wél mag doorzetten. */}
        {viewerIsHrOrAdmin && employeeUitDienstDatum && (
          <div
            data-testid="correctie-uit-dienst-waarschuwing"
            role="status"
            className="mb-4 px-3 py-2 rounded-md bg-[#54949E]/10 text-[#1E293B] text-sm"
          >
            {`${employeeName ?? 'Deze medewerker'} is uit dienst per ${formatNlDagEnMaand(
              employeeUitDienstDatum
            )}. Alleen HR kan dit nog aanpassen.`}
          </div>
        )}

        <div
          className="mb-4 px-3 py-2 rounded-md border border-[#E2E8F0] bg-gray-50"
          data-testid="correctie-goedgekeurd-kader"
          aria-readonly="true"
          role="group"
          aria-label="Goedgekeurd, niet te bewerken"
        >
          <p className="text-xs font-medium text-gray-500 mb-1">Goedgekeurd</p>
          {usesTijden ? (
            <p className="text-sm text-gray-700">
              {historyEntry.startTime} – {historyEntry.endTime}
            </p>
          ) : (
            <p className="text-sm text-gray-700">{formatNlUren(historyEntry.hours)} uur</p>
          )}
        </div>

        {usesTijden ? (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label htmlFor="correctie-van-tijd" className="block text-xs text-gray-500 mb-1">
                  Van
                </label>
                <input
                  id="correctie-van-tijd"
                  type="time"
                  step={300}
                  value={vanTijd}
                  onChange={(e) => {
                    setVanTijd(e.target.value);
                    // AC-1: opnieuw een tijd invullen betekent dat er weer een
                    // echt tijdvak bedoeld wordt, niet meer "ging niet door".
                    setNulViaKnop(false);
                    setTouched(true);
                  }}
                  aria-describedby="correctie-bovengrens-fout"
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
                />
              </div>
              <div>
                <label htmlFor="correctie-tot-tijd" className="block text-xs text-gray-500 mb-1">
                  Tot
                </label>
                <input
                  id="correctie-tot-tijd"
                  type="time"
                  step={300}
                  value={totTijd}
                  onChange={(e) => {
                    setTotTijd(e.target.value);
                    setNulViaKnop(false);
                    setTouched(true);
                  }}
                  aria-describedby="correctie-bovengrens-fout"
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
                />
              </div>
            </div>

            {/* AC-1 (B23): alleen bij tijden-gebaseerd verlof -- "het ging
                niet door" is geen tijdvak en een venster van nul uur wordt
                door FR-2 juist geweigerd, dus dit is de enige ingang. */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => {
                  setNulViaKnop((v) => !v);
                  setTouched(true);
                }}
                aria-pressed={nulViaKnop}
                data-testid="correctie-ging-niet-door-knop"
                className={`px-3 py-2 rounded-md border text-sm font-medium ${
                  nulViaKnop
                    ? 'border-[#2F5A7A] bg-[#2F5A7A]/10 text-[#2F5A7A]'
                    : 'border-[#E2E8F0] text-gray-700'
                }`}
              >
                Ging helemaal niet door
              </button>
            </div>
          </>
        ) : (
          <div className="mb-4">
            <label htmlFor="correctie-uren" className="block text-sm font-medium text-gray-700 mb-1">
              Uren
            </label>
            <input
              id="correctie-uren"
              type="number"
              min={0}
              step={0.25}
              value={hoursInput}
              onChange={(e) => {
                setHoursInput(e.target.value);
                setTouched(true);
              }}
              aria-describedby="correctie-bovengrens-fout"
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
            />
          </div>
        )}

        {diff !== null && diff !== 0 && (
          <p
            data-testid="correctie-verschil"
            className={`mb-2 text-sm font-medium ${diff < 0 ? 'text-[#5C7A00]' : 'text-[#2F5A7A]'}`}
          >
            {diff < 0 ? `▼ ${formatNlUren(Math.abs(diff))} uur minder` : `▲ ${formatNlUren(diff)} uur meer`}
          </p>
        )}

        {/* AC-9: de verandering van de uitkomst wordt als tekst aangekondigd,
            niet alleen via kleur -- aria-live zorgt dat een screenreader het
            omslagpunt naar "vervalt" meekrijgt zonder dat er gefocust hoeft
            te worden. */}
        {newHours !== null && (
          <div aria-live="polite" className="mb-4">
            {newHours === 0 ? (
              <>
                <p className="text-sm font-medium text-[#D64545]">
                  Wordt: 0 uur — dit verlof vervalt
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Na goedkeuring verdwijnt deze dag uit het teamoverzicht en komen de uren terug op
                  je saldo.
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-700">{`Wordt: ${formatNlUren(newHours)} uur`}</p>
            )}
          </div>
        )}

        {exceedsMax && maxUren !== null && (
          <p id="correctie-bovengrens-fout" data-testid="correctie-bovengrens-fout" role="alert" className="mb-4 text-xs text-[#D64545]">
            {grossHours !== null
              ? `Meer dan je die dag werkt. Je werkte ${formatNlUren(grossHours)} uur op ${formatNlDatum(
                  historyEntry.startDate
                )}, dus je kunt hoogstens ${formatNlUren(maxUren)} uur verlof opnemen (de pauze telt niet mee).`
              : // AC-11: geen rooster bekend -- de bestaande goedgekeurde waarde is
                // dan het plafond, maar zonder roosterspan is er geen "je werkte
                // X uur op [datum]" om te noemen (code review 2026-08-08, Finding #3).
                `Meer dan toegestaan. Je kunt hoogstens ${formatNlUren(maxUren)} uur verlof opnemen.`}
          </p>
        )}

        <div className="mb-4">
          <label htmlFor="correctie-toelichting" className="block text-sm font-medium text-gray-700 mb-1">
            Waarom pas je dit aan?
          </label>
          <textarea
            id="correctie-toelichting"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setTouched(true);
            }}
            onBlur={() => setTouched(true)}
            rows={2}
            aria-describedby="correctie-toelichting-fout"
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
          />
          {showReasonError && (
            <p id="correctie-toelichting-fout" data-testid="correctie-toelichting-fout" role="alert" className="mt-1 text-xs text-[#D64545]">
              Vul kort in waarom je dit aanpast.
            </p>
          )}
        </div>

        <p className="mb-6 text-xs text-gray-500" data-testid="correctie-beoordelingsmelding">
          {isTeamManagerOrHR
            ? 'Deze wijziging wordt door HR beoordeeld.'
            : 'Je leidinggevende beoordeelt deze wijziging.'}
        </p>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full px-4 py-2 rounded-md bg-[#2F5A7A] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {newHours === 0 ? 'Verlof laten vervallen' : 'Indienen'}
        </button>
      </div>
    </div>
  );
}
